import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createOnyxTheme } from '../theme/muiTheme';

const STORAGE_KEY = 'onyx-theme-mode';

const ThemeModeContext = createContext({
  mode: 'light',
  preference: 'light',
  allowsDarkMode: false,
  setMode: () => {},
  toggleMode: () => {},
});

/** Admin operator surfaces only — member / marketing / onboarding stay light. */
export function isAdminThemePath(pathname = '') {
  return typeof pathname === 'string' && pathname.startsWith('/admin');
}

/**
 * Persist light|dark preference for admin routes.
 * Effective mode is always light outside /admin/*.
 */
const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    if (stored) {
      window.localStorage.setItem(STORAGE_KEY, 'light');
    }
  } catch {
    /* ignore */
  }
  return 'light';
};

export function ThemeModeProvider({ children }) {
  const location = useLocation();
  const [preference, setPreference] = useState(readStoredMode);
  const allowsDarkMode = isAdminThemePath(location.pathname);
  const mode = allowsDarkMode && preference === 'dark' ? 'dark' : 'light';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const setMode = useCallback((next) => {
    const value = next === 'dark' ? 'dark' : 'light';
    // Prefer is always stored; effective dark only applies on /admin/*
    setPreference(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleMode = useCallback(() => {
    if (!allowsDarkMode) return;
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [allowsDarkMode, mode, setMode]);

  const theme = useMemo(() => createOnyxTheme(mode), [mode]);

  const value = useMemo(
    () => ({ mode, preference, allowsDarkMode, setMode, toggleMode }),
    [mode, preference, allowsDarkMode, setMode, toggleMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

ThemeModeProvider.propTypes = {
  children: PropTypes.node,
};

export function useThemeMode() {
  return useContext(ThemeModeContext);
}
